import React from 'react';
import { Box, Button, Chip, Paper, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';

export default function ProjectRecordDetail({ title, subtitle, rows, onEdit }) {
    return (
        <Paper component="section" variant="outlined" sx={{ borderRadius: 1, overflow: 'hidden', boxShadow: 'none' }}>
            <Box sx={{ px: { xs: 1.5, sm: 2 }, py: 1.35, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Stack direction="row" spacing={1.5} alignItems="flex-start" justifyContent="space-between">
                    <Box sx={{ minWidth: 0 }}>
                        <Typography component="h2" sx={{ fontSize: 16, lineHeight: 1.25, fontWeight: 850 }}>
                            {title}
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block', mt: 0.25, color: 'text.secondary' }}>
                            {subtitle}
                        </Typography>
                    </Box>
                    <Chip size="small" label="Project scope" sx={{ height: 24, flexShrink: 0, fontSize: 11, fontWeight: 750 }} />
                </Stack>
            </Box>

            <Typography
                variant="caption"
                sx={{
                    display: 'block',
                    px: { xs: 1.5, sm: 2 },
                    py: 0.9,
                    color: (theme) => theme.palette.mode === 'dark' ? theme.palette.success.light : theme.palette.success.dark,
                    backgroundColor: (theme) => alpha(theme.palette.success.main, theme.palette.mode === 'dark' ? 0.13 : 0.09)
                }}
            >
                Each value retains its source and current verification state.
            </Typography>

            <Box>
                {rows.map((row, index) => {
                    const hasValue = row.value !== null && row.value !== undefined && String(row.value).trim() !== '';
                    const action = typeof row.onAction === 'function' ? row.onAction : onEdit;
                    const actionLabel = row.actionLabel || (hasValue ? 'Edit' : 'Add');
                    return (
                        <Box
                            key={row.key || row.label}
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr auto', sm: '125px minmax(0, 1fr) minmax(150px, auto) auto' },
                                gap: { xs: 0.6, sm: 1.5 },
                                alignItems: 'center',
                                px: { xs: 1.5, sm: 2 },
                                py: { xs: 1.4, sm: 1.7 },
                                borderTop: index ? '1px solid' : 'none',
                                borderColor: 'divider'
                            }}
                        >
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700 }}>
                                {row.label}
                            </Typography>
                            <Typography
                                sx={{
                                    gridColumn: { xs: '1 / -1', sm: 'auto' },
                                    gridRow: { xs: 2, sm: 'auto' },
                                    minWidth: 0,
                                    fontSize: 13,
                                    fontWeight: hasValue ? 750 : 600,
                                    color: hasValue ? 'text.primary' : 'text.secondary',
                                    overflowWrap: 'anywhere'
                                }}
                            >
                                {hasValue ? typeof row.value === 'boolean' ? row.value ? 'Yes' : 'No' : String(row.value) : 'Not available'}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary', textAlign: { sm: 'right' } }}>
                                {hasValue ? (row.source || 'Stored value - verification not confirmed') : (row.missingLabel || 'Awaiting confirmation')}
                            </Typography>
                            {action ? (
                                <Button
                                    size="small"
                                    variant="outlined"
                                    aria-label={`${actionLabel} ${row.label}`}
                                    onClick={() => action(row)}
                                    sx={{ minWidth: 42, px: 1, justifySelf: 'end' }}
                                >
                                    {actionLabel}
                                </Button>
                            ) : null}
                        </Box>
                    );
                })}
            </Box>
        </Paper>
    );
}